import { ChangeEvent, useState } from 'react'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import * as XLSX from 'xlsx'
import { importLuminaPeople, LuminaImportResponse, LuminaImportRow, LuminaPerson } from './services'

interface Props {
  onClose: () => void
  onImported: (people: LuminaPerson[]) => void
}

const statusLabels: Record<string, string> = {
  updated: 'Actualizada',
  duplicate: 'Duplicada',
  invalid: 'Inválida',
  not_found: 'No encontrada',
  error: 'Error al guardar',
}

const ImportPeopleDialog = ({ onClose, onImported }: Props) => {
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<LuminaImportRow[]>([])
  const [result, setResult] = useState<LuminaImportResponse | null>(null)
  const [error, setError] = useState('')
  const [phase, setPhase] = useState<'idle' | 'reading' | 'saving'>('idle')

  const selectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setRows([])
    setResult(null)
    setError('')
    setFileName(file?.name || '')
    if (!file) return
    if (!/\.xlsx$/i.test(file.name)) {
      setError('Selecciona un archivo .xlsx.')
      return
    }

    setPhase('reading')
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      if (!sheet) throw new Error('El archivo no tiene una hoja de datos.')
      const values = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: false, blankrows: true })
      const headers = (values[0] || []).map((value) => String(value ?? '').trim().toLocaleLowerCase('es'))
      if (headers.length !== 3 || headers[0] !== 'documento' || headers[1] !== 'cargo' || headers[2] !== 'desempeño') {
        throw new Error('La primera fila debe contener exactamente: documento, cargo, desempeño.')
      }
      const data = values.slice(1).map((value, index) => ({
        row_number: index + 2,
        document: String(value[0] ?? '').trim(),
        cargo: String(value[1] ?? '').trim(),
        desempeno: String(value[2] ?? '').trim(),
      })).filter((row) => row.document || row.cargo || row.desempeno)
      if (!data.length) throw new Error('El archivo no contiene personas para importar.')
      if (data.length > 5000) throw new Error('El archivo supera el límite de 5000 filas.')
      setRows(data)
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : 'No se pudo leer el Excel.')
    } finally {
      setPhase('idle')
    }
  }

  const save = async () => {
    if (!rows.length || phase !== 'idle') return
    setPhase('saving')
    setError('')
    try {
      const response = await importLuminaPeople(rows)
      setResult(response)
      const importedPeople = response.results.flatMap((item) => item.status === 'updated' && item.person ? [item.person] : [])
      onImported(importedPeople)
    } catch (saveError: any) {
      setError(saveError?.response?.data?.message || 'No fue posible importar los datos. Inténtalo de nuevo.')
    } finally {
      setPhase('idle')
    }
  }

  return (
    <Dialog open onClose={phase === 'idle' ? onClose : undefined} fullWidth maxWidth="md">
      <DialogTitle>Importar Excel de personas</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>Columnas: documento, cargo y desempeño (1,00 a 10,00). La persona debe pertenecer a la empresa y tener BETESA o 3D Skills finalizada.</Typography>
        <Button href={`${process.env.PUBLIC_URL}/plantilla-personas-lumina.xlsx`} download="plantilla-personas-lumina.xlsx" sx={{ mb: 2 }}>Descargar plantilla Excel</Button>
        <Box><input type="file" accept=".xlsx" onChange={selectFile} disabled={phase !== 'idle'} aria-label="Seleccionar Excel de personas" /></Box>
        {fileName && <Typography sx={{ mt: 1 }}>{fileName}</Typography>}
        {phase !== 'idle' && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}><CircularProgress size={20} />{phase === 'reading' ? 'Leyendo Excel…' : 'Guardando filas…'}</Box>}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        {rows.length > 0 && !result && <Typography sx={{ mt: 2 }}>He leído {rows.length} filas. Pulsa «Importar» para validarlas y guardarlas.</Typography>}
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography fontWeight={700}>Filas leídas: {result.total}. Actualizadas: {result.updated}. Rechazadas: {result.total - result.updated}.</Typography>
            <Typography color="text.secondary">
              No encontradas: {result.results.filter((item) => item.status === 'not_found').length}. Inválidas: {result.results.filter((item) => item.status === 'invalid').length}. Duplicadas: {result.results.filter((item) => item.status === 'duplicate').length}. Errores de guardado: {result.results.filter((item) => item.status === 'error').length}.
            </Typography>
            <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow><TableCell>Fila</TableCell><TableCell>Documento</TableCell><TableCell>Resultado</TableCell><TableCell>Detalle</TableCell></TableRow></TableHead>
                <TableBody>{result.results.map((item) => <TableRow key={item.row_number}><TableCell>{item.row_number}</TableCell><TableCell>{item.document || 'Vacío'}</TableCell><TableCell>{statusLabels[item.status]}</TableCell><TableCell>{item.reason || item.name || ''}</TableCell></TableRow>)}</TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={phase !== 'idle'}>{result ? 'Cerrar' : 'Cancelar'}</Button>
        {!result && <Button variant="contained" onClick={save} disabled={!rows.length || phase !== 'idle'}>Importar</Button>}
      </DialogActions>
    </Dialog>
  )
}

export default ImportPeopleDialog
